import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Calendar, CalendarEvent, CalendarEventInput, Task } from "@websede/contracts";
import {
  AlignLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Plus,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { ErrorNotice } from "../components/form.js";
import { ShellSidebar, useShell } from "../components/shell-slots.js";

const HOUR_PX = 48;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EDITABLE = /^(INPUT|TEXTAREA|SELECT)$/;
const VIEW_KEY = "websede.calendar.view";
const HIDDEN_KEY = "websede.calendar.hidden";

/** Google Calendar's event colours; a calendar without its own colour takes one by position. */
const PALETTE = [
  "#0f6e6b",
  "#039be5",
  "#8e24aa",
  "#e67c73",
  "#33b679",
  "#f4511e",
  "#3f51b5",
  "#f6bf26",
];

export type View = "day" | "threeDays" | "week" | "month" | "schedule";
const VIEWS: View[] = ["day", "threeDays", "week", "month", "schedule"];

interface Draft extends CalendarEventInput {
  id?: string;
}

interface Timed {
  event: CalendarEvent;
  start: Date;
  end: Date;
}

function readView(): View {
  try {
    const saved = localStorage.getItem(VIEW_KEY) as View | null;
    if (saved && VIEWS.includes(saved)) return saved;
  } catch {
    // Storage blocked: fall through to the default for the screen size.
  }
  return window.matchMedia("(min-width: 768px)").matches ? "week" : "schedule";
}

function readHidden(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** The first and the day after the last day a view shows. */
export function viewRange(view: View, anchor: Date): { start: Date; end: Date; days: number } {
  const day = startOfDay(anchor);
  switch (view) {
    case "day":
      return { start: day, end: addDays(day, 1), days: 1 };
    case "threeDays":
      return { start: day, end: addDays(day, 3), days: 3 };
    case "week": {
      const start = startOfWeek(day);
      return { start, end: addDays(start, 7), days: 7 };
    }
    case "month": {
      const start = startOfWeek(new Date(day.getFullYear(), day.getMonth(), 1));
      return { start, end: addDays(start, 42), days: 42 };
    }
    case "schedule":
      return { start: day, end: addDays(day, 30), days: 30 };
  }
}

function step(view: View, anchor: Date, direction: 1 | -1): Date {
  if (view === "month") return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
  const size = { day: 1, threeDays: 3, week: 7, schedule: 30 }[view];
  return addDays(anchor, size * direction);
}

/**
 * Places overlapping timed events side by side: each event gets a lane and the number of lanes
 * in its cluster of overlaps, like Google Calendar's columns.
 */
export function layoutDay<T extends { start: Date; end: Date }>(
  items: T[],
): Array<T & { lane: number; lanes: number }> {
  const sorted = [...items].sort(
    (a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime(),
  );
  const result: Array<T & { lane: number; lanes: number }> = [];
  let cluster: Array<T & { lane: number; lanes: number }> = [];
  let laneEnds: number[] = [];
  let clusterEnd = 0;
  const flush = () => {
    for (const item of cluster) item.lanes = laneEnds.length;
    result.push(...cluster);
    cluster = [];
    laneEnds = [];
  };
  for (const item of sorted) {
    const start = item.start.getTime();
    if (cluster.length > 0 && start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = item.end.getTime();
    clusterEnd = Math.max(clusterEnd, item.end.getTime());
    cluster.push({ ...item, lane, lanes: 1 });
  }
  flush();
  return result;
}

export function CalendarPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { closeDrawer } = useShell();
  const [view, setViewState] = useState<View>(readView);
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(readHidden);
  const range = useMemo(() => viewRange(view, anchor), [view, anchor]);

  const setView = useCallback((next: View) => {
    setViewState(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Not remembered this time.
    }
  }, []);
  const toggleHidden = (id: string) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next]));
      } catch {
        // Not remembered this time.
      }
      return next;
    });
  };

  const calendars = useQuery({
    queryKey: ["calendar", "calendars"],
    queryFn: () => api.calendar.calendars(),
  });
  const events = useQuery({
    queryKey: ["calendar", "events", range.start.toISOString(), range.end.toISOString()],
    queryFn: () => api.calendar.events(toLocal(range.start), toLocal(range.end), ZONE),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["calendar", "events"] });
  const tasks = useQuery({ queryKey: ["tasks", "all"], queryFn: () => api.tasks.list() });

  const colours = useMemo(() => {
    const map = new Map<string, string>();
    (calendars.data?.calendars ?? []).forEach((c, index) => {
      map.set(c.id, c.color || PALETTE[index % PALETTE.length] || PALETTE[0] || "#0f6e6b");
    });
    return map;
  }, [calendars.data]);
  const colourOf = useCallback(
    (event: CalendarEvent) => colours.get(event.calendarId) ?? "#0f6e6b",
    [colours],
  );

  const visible = useMemo(
    () => (events.data?.events ?? []).filter((e) => !hidden.has(e.calendarId)),
    [events.data, hidden],
  );
  const tasksByDay = useMemo(() => {
    const byDay = new Map<string, Task[]>();
    for (const task of tasks.data?.tasks ?? []) {
      if (!task.due || task.completed) continue;
      const key = task.due.slice(0, 10);
      byDay.set(key, [...(byDay.get(key) ?? []), task]);
    }
    return byDay;
  }, [tasks.data]);

  const defaultCalendar =
    calendars.data?.calendars.find((c) => c.isDefault) ?? calendars.data?.calendars[0];
  const newDraft = useCallback(
    (start: Date, allDay = false): Draft => ({
      calendarId: defaultCalendar?.id ?? "",
      title: "",
      description: null,
      location: null,
      start: toLocal(start),
      timeZone: ZONE,
      durationMinutes: allDay ? 1440 : 60,
      allDay,
    }),
    [defaultCalendar],
  );
  const create = useCallback(() => {
    const now = new Date();
    const base = sameDay(anchor, now) ? nextHour() : new Date(anchor.getTime() + 9 * 3_600_000);
    setDraft(newDraft(base));
  }, [anchor, newDraft]);
  const openEvent = useCallback((event: CalendarEvent) => setDraft({ ...event }), []);
  const goToday = useCallback(() => setAnchor(startOfDay(new Date())), []);
  const move = useCallback(
    (direction: 1 | -1) => setAnchor((a) => step(view, a, direction)),
    [view],
  );
  const openDay = (day: Date) => {
    setAnchor(startOfDay(day));
    setView("day");
  };

  // Google Calendar's single-key shortcuts.
  useEffect(() => {
    if (draft) return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && EDITABLE.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const actions: Record<string, () => void> = {
        t: goToday,
        j: () => move(1),
        n: () => move(1),
        k: () => move(-1),
        p: () => move(-1),
        c: create,
        d: () => setView("day"),
        x: () => setView("threeDays"),
        w: () => setView("week"),
        m: () => setView("month"),
        a: () => setView("schedule"),
      };
      const action = actions[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [draft, goToday, move, create, setView]);

  const title = rangeTitle(view, anchor, range, i18n.language);

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <ShellSidebar>
        <div className="hidden px-2 pt-1 pb-3 md:block">
          <button
            type="button"
            onClick={create}
            className="flex h-14 items-center gap-3 rounded-2xl bg-accent-soft pr-6 pl-4 text-sm font-medium text-accent hover:shadow-1"
          >
            <Plus className="size-5" />
            {t("calendar.create")}
          </button>
        </div>
        <MiniMonth
          anchor={anchor}
          rangeStart={view === "month" ? anchor : range.start}
          rangeEnd={view === "month" ? anchor : range.end}
          onPick={(day) => {
            setAnchor(day);
            closeDrawer();
          }}
        />
        <div className="mt-4 px-4">
          <div className="px-2 pb-1 text-sm font-medium text-ink">{t("calendar.myCalendars")}</div>
          {(calendars.data?.calendars ?? []).map((c) => (
            <label
              key={c.id}
              className="flex h-9 cursor-pointer items-center gap-3 rounded-full px-2 text-sm text-ink-2 hover:bg-ink/5"
            >
              <input
                type="checkbox"
                className="size-4 cursor-pointer"
                style={{ accentColor: colours.get(c.id) }}
                checked={!hidden.has(c.id)}
                onChange={() => toggleHidden(c.id)}
              />
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
            </label>
          ))}
        </div>
      </ShellSidebar>

      <header className="flex h-14 shrink-0 items-center gap-1 px-2 md:gap-2 md:px-4">
        <button
          type="button"
          onClick={goToday}
          className="h-9 rounded-full border border-rule px-4 text-sm text-ink-2 hover:bg-surface-2"
        >
          {t("calendar.today")}
        </button>
        <button
          type="button"
          aria-label={t("calendar.previous")}
          onClick={() => move(-1)}
          className="flex size-9 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
        >
          <ChevronLeft className="size-5" />
        </button>
        <button
          type="button"
          aria-label={t("calendar.next")}
          onClick={() => move(1)}
          className="flex size-9 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
        >
          <ChevronRight className="size-5" />
        </button>
        <h1 className="min-w-0 flex-1 truncate px-1 text-lg text-ink md:text-[22px]">{title}</h1>
        <select
          aria-label={t("calendar.view")}
          value={view}
          onChange={(e) => setView(e.target.value as View)}
          className="h-9 rounded-full border border-rule bg-surface px-3 text-sm text-ink-2 outline-none hover:bg-surface-2"
        >
          {VIEWS.map((v) => (
            <option key={v} value={v}>
              {t(`calendar.views.${v}`)}
            </option>
          ))}
        </select>
      </header>
      <ErrorNotice error={events.error ?? calendars.error} />

      {view === "month" ? (
        <MonthGrid
          anchor={anchor}
          start={range.start}
          events={visible}
          tasksByDay={tasksByDay}
          colourOf={colourOf}
          onDay={openDay}
          onEvent={openEvent}
          onCreate={(day) => setDraft(newDraft(day, true))}
        />
      ) : view === "schedule" ? (
        <ScheduleList
          start={range.start}
          days={range.days}
          events={visible}
          tasksByDay={tasksByDay}
          colourOf={colourOf}
          onDay={openDay}
          onEvent={openEvent}
        />
      ) : (
        <TimeGrid
          start={range.start}
          days={range.days}
          events={visible}
          tasksByDay={tasksByDay}
          colourOf={colourOf}
          onDay={openDay}
          onSlot={(at, allDay) => setDraft(newDraft(at, allDay))}
          onEvent={openEvent}
        />
      )}

      {draft ? null : (
        <button
          type="button"
          aria-label={t("calendar.create")}
          onClick={create}
          className="absolute right-4 bottom-4 flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent shadow-2 md:hidden"
        >
          <Plus className="size-6" />
        </button>
      )}

      {draft ? (
        <EventEditor
          draft={draft}
          calendars={calendars.data?.calendars ?? []}
          colours={colours}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function rangeTitle(
  view: View,
  anchor: Date,
  range: { start: Date; end: Date },
  locale: string,
): string {
  if (view === "day") {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(anchor);
  }
  if (view === "month") {
    return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(anchor);
  }
  const last = addDays(range.end, -1);
  const month = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
  if (range.start.getMonth() === last.getMonth()) return month.format(range.start);
  const short = new Intl.DateTimeFormat(locale, { month: "short" });
  return `${short.format(range.start)} – ${short.format(last)} ${last.getFullYear()}`;
}

/** The small month in the side column; picking a day moves the main view there. */
function MiniMonth({
  anchor,
  rangeStart,
  rangeEnd,
  onPick,
}: {
  anchor: Date;
  rangeStart: Date;
  rangeEnd: Date;
  onPick: (day: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  useEffect(() => setMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1)), [anchor]);
  const first = startOfWeek(month);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(first, i));
  const today = startOfDay(new Date());
  const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: "narrow" });
  return (
    <div className="px-4">
      <div className="flex items-center px-2">
        <span className="flex-1 text-sm font-medium text-ink">
          {new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric" }).format(month)}
        </span>
        <button
          type="button"
          aria-label={t("calendar.previous")}
          className="flex size-7 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          aria-label={t("calendar.next")}
          className="flex size-7 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className="mt-1 grid grid-cols-7 text-center text-[11px]">
        {cells.slice(0, 7).map((d) => (
          <span key={`h-${d.getDay()}`} className="py-1 text-ink-3">
            {weekday.format(d)}
          </span>
        ))}
        {cells.map((d) => {
          const isToday = sameDay(d, today);
          const inRange = d >= rangeStart && d < rangeEnd;
          const otherMonth = d.getMonth() !== month.getMonth();
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => onPick(d)}
              className={`mx-auto flex size-7 items-center justify-center rounded-full ${isToday ? "bg-accent text-white" : inRange ? "bg-accent-soft text-accent" : otherMonth ? "text-ink-3 hover:bg-ink/5" : "text-ink-2 hover:bg-ink/5"}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DayHeader({ day, onDay, compact }: { day: Date; onDay: () => void; compact: boolean }) {
  const { i18n } = useTranslation();
  const isToday = sameDay(day, new Date());
  return (
    <button
      type="button"
      onClick={onDay}
      className="flex w-full flex-col items-center gap-0.5 py-1"
    >
      <span
        className={`text-[11px] uppercase ${isToday ? "font-medium text-accent" : "text-ink-3"}`}
      >
        {new Intl.DateTimeFormat(i18n.language, { weekday: "short" }).format(day)}
      </span>
      <span
        className={`flex items-center justify-center rounded-full ${compact ? "size-8 text-base" : "size-11 text-2xl"} ${isToday ? "bg-accent text-white" : "text-ink hover:bg-ink/5"}`}
      >
        {day.getDate()}
      </span>
    </button>
  );
}

function TaskChip({ task }: { task: Task }) {
  return (
    <Link
      to="/tasks"
      search={{ list: task.listId, task: task.id }}
      className="flex items-center gap-1 truncate rounded-md border border-rule bg-surface px-1.5 py-0.5 text-[11px] text-ink-2 hover:border-accent"
    >
      <Square className="size-3 shrink-0" />
      <span className="truncate">{task.title}</span>
    </Link>
  );
}

/** Day, three-day and week views: day columns on an hour grid, all-day items above. */
function TimeGrid({
  start,
  days,
  events,
  tasksByDay,
  colourOf,
  onDay,
  onSlot,
  onEvent,
}: {
  start: Date;
  days: number;
  events: CalendarEvent[];
  tasksByDay: Map<string, Task[]>;
  colourOf: (event: CalendarEvent) => string;
  onDay: (day: Date) => void;
  onSlot: (at: Date, allDay: boolean) => void;
  onEvent: (event: CalendarEvent) => void;
}) {
  const { t, i18n } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  // Open at the working day rather than midnight.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll again when the range changes
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 7 * HOUR_PX + HOUR_PX / 2;
  }, [start.getTime(), days]);

  const columns = useMemo(() => {
    const list = Array.from({ length: days }, (_, i) => ({
      day: addDays(start, i),
      allDay: [] as CalendarEvent[],
      timed: [] as Timed[],
    }));
    for (const event of events) {
      const from = toInstant(event.start, event.timeZone);
      const to = new Date(from.getTime() + event.durationMinutes * 60_000);
      if (event.allDay) {
        for (const column of list) {
          if (column.day >= startOfDay(from) && column.day < to) column.allDay.push(event);
        }
        continue;
      }
      const column = list.find((c) => sameDay(c.day, from));
      if (column) column.timed.push({ event, start: from, end: to });
    }
    return list.map((c) => ({ ...c, placed: layoutDay(c.timed) }));
  }, [start, days, events]);

  const hourLabel = new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" });
  const time = new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" });
  const template = `56px repeat(${days}, minmax(0, 1fr))`;
  const compact = days > 3;

  return (
    <div ref={scroller} className="min-h-0 flex-1 overflow-auto">
      <div className={days === 7 ? "min-w-[640px]" : ""}>
        <div
          className="sticky top-0 z-20 grid border-b border-rule bg-surface"
          style={{ gridTemplateColumns: template }}
        >
          <div className="flex items-end justify-end pr-2 pb-1 text-[10px] text-ink-3">
            {Intl.DateTimeFormat()
              .resolvedOptions()
              .timeZone.split("/")
              .pop()
              ?.replaceAll("_", " ")}
          </div>
          {columns.map((c) => {
            const key = toLocal(c.day).slice(0, 10);
            return (
              <div key={key} className="min-w-0 border-l border-rule px-1 pb-1">
                <DayHeader day={c.day} onDay={() => onDay(c.day)} compact={compact} />
                <div className="flex min-h-6 flex-col gap-0.5">
                  {(tasksByDay.get(key) ?? []).map((task) => (
                    <TaskChip key={task.id} task={task} />
                  ))}
                  {c.allDay.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => onEvent(event)}
                      className="truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium text-white"
                      style={{ background: colourOf(event) }}
                    >
                      {event.title || t("calendar.untitled")}
                    </button>
                  ))}
                  <button
                    type="button"
                    aria-label={t("calendar.addAllDay")}
                    className="h-4 w-full rounded hover:bg-ink/5"
                    onClick={() => onSlot(c.day, true)}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid" style={{ gridTemplateColumns: template }}>
          <div>
            {HOURS.map((h) => (
              <div key={h} className="relative pr-2 text-right" style={{ height: HOUR_PX }}>
                {h > 0 ? (
                  <span className="relative -top-2 text-[10px] text-ink-3">
                    {hourLabel.format(new Date(2000, 0, 1, h))}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          {columns.map((c) => {
            const isToday = sameDay(c.day, now);
            const nowTop = ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_PX;
            return (
              <div key={c.day.toISOString()} className="relative border-l border-rule">
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    aria-label={`${c.day.toDateString()} ${h}:00`}
                    className="block w-full border-b border-rule/70 hover:bg-ink/[0.03]"
                    style={{ height: HOUR_PX }}
                    onClick={() => onSlot(new Date(c.day.getTime() + h * 3_600_000), false)}
                  />
                ))}
                {c.placed.map(({ event, start: from, end: to, lane, lanes }) => {
                  const top = ((from.getHours() * 60 + from.getMinutes()) / 60) * HOUR_PX;
                  const dayEnd = 24 * HOUR_PX;
                  const height = Math.max(
                    20,
                    Math.min((event.durationMinutes / 60) * HOUR_PX, dayEnd - top) - 2,
                  );
                  const width = 100 / lanes;
                  return (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => onEvent(event)}
                      className="absolute overflow-hidden rounded-md border border-surface px-1.5 py-0.5 text-left text-[11px] leading-tight text-white hover:z-10 hover:shadow-2"
                      style={{
                        top,
                        height,
                        left: `calc(${lane * width}% + 1px)`,
                        width: `calc(${width}% - 4px)`,
                        background: colourOf(event),
                      }}
                    >
                      <div className="truncate font-medium">
                        {event.title || t("calendar.untitled")}
                      </div>
                      {height > 30 ? (
                        <div className="truncate opacity-90">
                          {time.format(from)} – {time.format(to)}
                        </div>
                      ) : null}
                    </button>
                  );
                })}
                {isToday ? (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-10 flex items-center"
                    style={{ top: nowTop - 5 }}
                  >
                    <span className="size-2.5 -translate-x-1/2 rounded-full bg-[#ea4335]" />
                    <span className="h-0.5 flex-1 bg-[#ea4335]" />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthGrid({
  anchor,
  start,
  events,
  tasksByDay,
  colourOf,
  onDay,
  onEvent,
  onCreate,
}: {
  anchor: Date;
  start: Date;
  events: CalendarEvent[];
  tasksByDay: Map<string, Task[]>;
  colourOf: (event: CalendarEvent) => string;
  onDay: (day: Date) => void;
  onEvent: (event: CalendarEvent) => void;
  onCreate: (day: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const byDay = useMemo(() => {
    const map = new Map<string, Timed[]>();
    for (const event of events) {
      const from = toInstant(event.start, event.timeZone);
      const to = new Date(from.getTime() + event.durationMinutes * 60_000);
      const lastDay = event.allDay ? addDays(startOfDay(to), -1) : startOfDay(from);
      for (let d = startOfDay(from); d <= lastDay; d = addDays(d, 1)) {
        const key = toLocal(d).slice(0, 10);
        map.set(key, [...(map.get(key) ?? []), { event, start: from, end: to }]);
      }
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          Number(b.event.allDay) - Number(a.event.allDay) || a.start.getTime() - b.start.getTime(),
      );
    }
    return map;
  }, [events]);
  const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
  const time = new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" });
  const today = startOfDay(new Date());
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid grid-cols-7 border-b border-rule">
        {cells.slice(0, 7).map((d) => (
          <span key={d.getDay()} className="py-1 text-center text-[11px] text-ink-3 uppercase">
            {weekday.format(d)}
          </span>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
        {cells.map((d) => {
          const key = toLocal(d).slice(0, 10);
          const items = byDay.get(key) ?? [];
          const dueTasks = tasksByDay.get(key) ?? [];
          const shown = items.slice(0, 3);
          const more = items.length - shown.length;
          const isToday = sameDay(d, today);
          return (
            <div
              key={key}
              className={`flex min-h-0 min-w-0 flex-col gap-0.5 overflow-hidden border-r border-b border-rule p-0.5 ${d.getMonth() === anchor.getMonth() ? "" : "bg-surface-2/50"}`}
            >
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => onDay(d)}
                  className={`flex size-6 items-center justify-center rounded-full text-xs ${isToday ? "bg-accent text-white" : d.getMonth() === anchor.getMonth() ? "text-ink hover:bg-ink/5" : "text-ink-3 hover:bg-ink/5"}`}
                >
                  {d.getDate()}
                </button>
              </div>
              {dueTasks.slice(0, 1).map((task) => (
                <TaskChip key={task.id} task={task} />
              ))}
              {shown.map(({ event, start: from }) =>
                event.allDay ? (
                  <button
                    key={`${event.id}-${key}`}
                    type="button"
                    onClick={() => onEvent(event)}
                    className="truncate rounded px-1 text-left text-[11px] font-medium text-white"
                    style={{ background: colourOf(event) }}
                  >
                    {event.title || t("calendar.untitled")}
                  </button>
                ) : (
                  <button
                    key={`${event.id}-${key}`}
                    type="button"
                    onClick={() => onEvent(event)}
                    className="flex min-w-0 items-center gap-1 rounded px-1 text-left text-[11px] text-ink-2 hover:bg-ink/5"
                  >
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: colourOf(event) }}
                    />
                    <span className="hidden shrink-0 sm:inline">{time.format(from)}</span>
                    <span className="truncate font-medium text-ink">
                      {event.title || t("calendar.untitled")}
                    </span>
                  </button>
                ),
              )}
              {more > 0 ? (
                <button
                  type="button"
                  onClick={() => onDay(d)}
                  className="rounded px-1 text-left text-[11px] font-medium text-ink-2 hover:bg-ink/5"
                >
                  {t("calendar.more", { count: more })}
                </button>
              ) : null}
              <button
                type="button"
                aria-label={t("calendar.addAllDay")}
                className="min-h-2 flex-1"
                onClick={() => onCreate(d)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The phone's default: upcoming days with something on them, as a list. */
function ScheduleList({
  start,
  days,
  events,
  tasksByDay,
  colourOf,
  onDay,
  onEvent,
}: {
  start: Date;
  days: number;
  events: CalendarEvent[];
  tasksByDay: Map<string, Task[]>;
  colourOf: (event: CalendarEvent) => string;
  onDay: (day: Date) => void;
  onEvent: (event: CalendarEvent) => void;
}) {
  const { t, i18n } = useTranslation();
  const time = new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" });
  const month = new Intl.DateTimeFormat(i18n.language, { weekday: "short", month: "short" });
  const rows = useMemo(() => {
    const list: Array<{ day: Date; items: Timed[]; tasks: Task[] }> = [];
    for (let i = 0; i < days; i++) {
      const day = addDays(start, i);
      const next = addDays(day, 1);
      const items = events
        .map((event) => {
          const from = toInstant(event.start, event.timeZone);
          return {
            event,
            start: from,
            end: new Date(from.getTime() + event.durationMinutes * 60_000),
          };
        })
        .filter((x) => (x.event.allDay ? x.start < next && x.end > day : sameDay(x.start, day)))
        .sort(
          (a, b) =>
            Number(b.event.allDay) - Number(a.event.allDay) ||
            a.start.getTime() - b.start.getTime(),
        );
      const tasks = tasksByDay.get(toLocal(day).slice(0, 10)) ?? [];
      if (items.length > 0 || tasks.length > 0 || i === 0) list.push({ day, items, tasks });
    }
    return list;
  }, [start, days, events, tasksByDay]);

  return (
    <div className="min-h-0 flex-1 overflow-auto px-2 pb-24 md:px-4">
      {rows.map(({ day, items, tasks }) => {
        const isToday = sameDay(day, new Date());
        return (
          <div key={day.toISOString()} className="flex gap-3 border-b border-rule/60 py-3">
            <button
              type="button"
              onClick={() => onDay(day)}
              className="flex w-14 shrink-0 flex-col items-center"
            >
              <span
                className={`flex size-10 items-center justify-center rounded-full text-xl ${isToday ? "bg-accent text-white" : "text-ink"}`}
              >
                {day.getDate()}
              </span>
              <span className={`text-[11px] uppercase ${isToday ? "text-accent" : "text-ink-3"}`}>
                {month.format(day)}
              </span>
            </button>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-1">
              {items.length === 0 && tasks.length === 0 ? (
                <span className="py-2 text-sm text-ink-3">{t("calendar.nothingPlanned")}</span>
              ) : null}
              {tasks.map((task) => (
                <TaskChip key={task.id} task={task} />
              ))}
              {items.map(({ event, start: from, end: to }) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => onEvent(event)}
                  className="flex min-w-0 flex-col rounded-lg px-3 py-2 text-left text-white"
                  style={{ background: colourOf(event) }}
                >
                  <span className="truncate text-sm font-medium">
                    {event.title || t("calendar.untitled")}
                  </span>
                  <span className="truncate text-xs opacity-90">
                    {event.allDay
                      ? t("calendar.allDay")
                      : `${time.format(from)} – ${time.format(to)}`}
                    {event.location ? ` · ${event.location}` : ""}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Google-style event dialog: a big title, then time, place, notes and calendar on icon rows. */
function EventEditor({
  draft,
  calendars,
  colours,
  onChange,
  onClose,
  onSaved,
}: {
  draft: Draft;
  calendars: Calendar[];
  colours: Map<string, string>;
  onChange: (next: Draft) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const save = useMutation({
    mutationFn: () => {
      const { id, ...input } = draft;
      return id ? api.calendar.update(id, input) : api.calendar.create(input);
    },
    onSuccess: onSaved,
  });
  const remove = useMutation({
    mutationFn: () => api.calendar.remove(draft.id ?? ""),
    onSuccess: onSaved,
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  const startDate = draft.start.slice(0, 10);
  const startTime = draft.start.slice(11, 16);
  const endTime = minutesToTime(timeToMinutes(startTime) + draft.durationMinutes);
  const row = "flex items-start gap-4 px-5 py-2";
  const field =
    "rounded-md bg-surface-2 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40";

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center md:items-center md:bg-black/30 md:p-6">
      <button
        type="button"
        aria-label={t("common.close")}
        className="absolute inset-0 hidden cursor-default md:block"
        onClick={onClose}
      />
      <form
        onSubmit={onSubmit}
        className="relative flex w-full flex-col overflow-auto bg-surface md:max-h-[90vh] md:max-w-[560px] md:rounded-2xl md:shadow-2"
      >
        <div className="flex h-14 shrink-0 items-center gap-1 px-2">
          <button
            type="button"
            aria-label={t("common.close")}
            className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
            onClick={onClose}
          >
            <X className="size-5" />
          </button>
          <span className="flex-1" />
          {draft.id ? (
            <button
              type="button"
              aria-label={t("calendar.delete")}
              title={t("calendar.delete")}
              disabled={remove.isPending}
              className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 hover:text-danger"
              onClick={() => remove.mutate()}
            >
              <Trash2 className="size-5" />
            </button>
          ) : null}
          <button
            type="submit"
            disabled={save.isPending}
            className="ml-1 h-9 rounded-full bg-accent px-6 text-sm font-medium text-white disabled:opacity-60"
          >
            {t("common.save")}
          </button>
        </div>
        <div className="px-5 pb-3 pl-[60px]">
          <input
            required
            // biome-ignore lint/a11y/noAutofocus: the title is the first thing to type in a new event
            autoFocus={!draft.id}
            aria-label={t("calendar.eventTitle")}
            placeholder={t("calendar.addTitle")}
            className="w-full border-b border-rule bg-transparent pb-2 text-[22px] text-ink outline-none focus:border-accent"
            value={draft.title}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
          />
        </div>
        <div className={row}>
          <Clock className="mt-2 size-5 shrink-0 text-ink-3" />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <input
              type="date"
              required
              aria-label={t("calendar.date")}
              className={field}
              value={startDate}
              onChange={(e) => onChange({ ...draft, start: `${e.target.value}T${startTime}:00` })}
            />
            {draft.allDay ? null : (
              <>
                <input
                  type="time"
                  required
                  aria-label={t("calendar.start")}
                  className={field}
                  value={startTime}
                  onChange={(e) =>
                    onChange({ ...draft, start: `${startDate}T${e.target.value}:00` })
                  }
                />
                <span className="text-ink-3">–</span>
                <input
                  type="time"
                  required
                  aria-label={t("calendar.end")}
                  className={field}
                  value={endTime}
                  onChange={(e) =>
                    onChange({
                      ...draft,
                      durationMinutes: Math.max(
                        5,
                        timeToMinutes(e.target.value) - timeToMinutes(startTime),
                      ),
                    })
                  }
                />
              </>
            )}
            <label className="flex w-full items-center gap-2 pt-1 text-sm text-ink-2">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent)]"
                checked={draft.allDay}
                onChange={(e) =>
                  onChange({
                    ...draft,
                    allDay: e.target.checked,
                    durationMinutes: e.target.checked ? 1440 : 60,
                    start: e.target.checked ? `${startDate}T00:00:00` : `${startDate}T09:00:00`,
                  })
                }
              />
              {t("calendar.allDay")}
            </label>
          </div>
        </div>
        <div className={row}>
          <MapPin className="mt-2 size-5 shrink-0 text-ink-3" />
          <input
            aria-label={t("calendar.location")}
            placeholder={t("calendar.addLocation")}
            className={`${field} min-w-0 flex-1`}
            value={draft.location ?? ""}
            onChange={(e) => onChange({ ...draft, location: e.target.value || null })}
          />
        </div>
        <div className={row}>
          <AlignLeft className="mt-2 size-5 shrink-0 text-ink-3" />
          <textarea
            aria-label={t("calendar.description")}
            placeholder={t("calendar.addDescription")}
            className={`${field} min-h-24 min-w-0 flex-1 resize-y`}
            value={draft.description ?? ""}
            onChange={(e) => onChange({ ...draft, description: e.target.value || null })}
          />
        </div>
        {calendars.length > 0 ? (
          <div className={row}>
            <CalendarDays className="mt-2 size-5 shrink-0 text-ink-3" />
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span
                className="size-3 shrink-0 rounded-full"
                style={{ background: colours.get(draft.calendarId) }}
              />
              <select
                aria-label={t("calendar.calendar")}
                className={`${field} min-w-0 flex-1`}
                value={draft.calendarId}
                onChange={(e) => onChange({ ...draft, calendarId: e.target.value })}
              >
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}
        <div className="px-5 pt-2 pb-5">
          <ErrorNotice error={save.error ?? remove.error} />
        </div>
      </form>
    </div>
  );
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Monday-based week start in the browser's zone. */
function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  const offset = (day.getDay() + 6) % 7;
  return addDays(day, -offset);
}

function nextHour(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1);
}

/** Local wall-clock string YYYY-MM-DDTHH:mm:ss in the browser's zone. */
function toLocal(date: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
}

/** Interprets a wall-clock string in `timeZone` and returns the instant. */
function toInstant(local: string, timeZone: string): Date {
  const guess = new Date(`${local.length === 16 ? `${local}:00` : local}Z`);
  if (Number.isNaN(guess.getTime())) return new Date(Number.NaN);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(guess);
  const get = (type: string) => Number(parts.find((x) => x.type === type)?.value ?? 0);
  const zoned = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return new Date(guess.getTime() - (zoned - guess.getTime()));
}

function timeToMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes: number): string {
  const clamped = Math.min(Math.max(minutes, 0), 23 * 60 + 59);
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}
